import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  LayoutDashboard, Brain, Kanban, Film, LineChart, Clock, ListTodo,
  Sparkles, BookOpen, Settings, Menu, Moon, Sun, Play, Square, Timer, Pause,
  Clapperboard, LogOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Section accent palette (HSL triplets) — cohesive, dark-glass tuned
export const SECTION_ACCENTS = {
  command: '190 90% 50%',    // cyan
  brainDump: '84 75% 55%',   // lime
  pipeline: '215 92% 62%',   // blue
  creatives: '292 84% 63%',  // fuchsia
  metrics: '152 70% 47%',    // emerald
  tasks: '38 92% 55%',       // amber
  time: '21 90% 58%',        // orange
  assistant: '262 85% 68%',  // violet
  sops: '346 80% 60%',       // rose
  settings: '215 15% 65%',   // slate
};

const NAV = [
  { to: '/', label: 'Command Center', icon: LayoutDashboard, testId: 'sidebar-nav-command-center', accent: SECTION_ACCENTS.command },
  { to: '/brain-dump', label: 'Brain Dump', icon: Brain, testId: 'sidebar-nav-brain-dump', accent: SECTION_ACCENTS.brainDump },
  { to: '/pipeline', label: 'Pipeline', icon: Kanban, testId: 'sidebar-nav-pipeline', accent: SECTION_ACCENTS.pipeline },
  { to: '/creatives', label: 'Creatives', icon: Film, testId: 'sidebar-nav-creatives', accent: SECTION_ACCENTS.creatives },
  { to: '/metrics', label: 'Metrics / PnL', icon: LineChart, testId: 'sidebar-nav-metrics', accent: SECTION_ACCENTS.metrics },
  { to: '/tasks', label: 'Tasks', icon: ListTodo, testId: 'sidebar-nav-tasks', accent: SECTION_ACCENTS.tasks },
  { to: '/time', label: 'Time Tracker', icon: Clock, testId: 'sidebar-nav-time', accent: SECTION_ACCENTS.time },
  { to: '/assistant', label: 'AI Assistant', icon: Sparkles, testId: 'sidebar-nav-assistant', accent: SECTION_ACCENTS.assistant },
  { to: '/sops', label: 'SOP Library', icon: BookOpen, testId: 'sidebar-nav-sops', accent: SECTION_ACCENTS.sops },
  { to: '/settings', label: 'Settings', icon: Settings, testId: 'sidebar-nav-settings', accent: SECTION_ACCENTS.settings },
];

const ROUTE_ACCENTS = [
  ['/brain-dump', SECTION_ACCENTS.brainDump],
  ['/pipeline', SECTION_ACCENTS.pipeline],
  ['/products', SECTION_ACCENTS.pipeline],
  ['/creatives', SECTION_ACCENTS.creatives],
  ['/metrics', SECTION_ACCENTS.metrics],
  ['/tasks', SECTION_ACCENTS.tasks],
  ['/time', SECTION_ACCENTS.time],
  ['/assistant', SECTION_ACCENTS.assistant],
  ['/sops', SECTION_ACCENTS.sops],
  ['/settings', SECTION_ACCENTS.settings],
];

const accentForPath = (pathname) => {
  for (const [prefix, accent] of ROUTE_ACCENTS) {
    if (pathname.startsWith(prefix)) return accent;
  }
  return SECTION_ACCENTS.command;
};

const fmtElapsed = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

const TimerWidget = () => {
  const { activeSession, elapsed, isPaused, clockIn, clockOut, pauseSession, resumeSession, stagesData } = useApp();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('Product Research');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const handleClockIn = async () => {
    setBusy(true);
    try {
      await clockIn(category, null, notes);
      toast.success(`Clocked in — ${category}`);
      setOpen(false);
      setNotes('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-in failed');
    } finally { setBusy(false); }
  };

  const handleClockOut = async () => {
    setBusy(true);
    try {
      const s = await clockOut('');
      toast.success(`Clocked out — ${s.duration_minutes} min of active work logged (${s.category})`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-out failed');
    } finally { setBusy(false); }
  };

  const handlePauseResume = async () => {
    setBusy(true);
    try {
      if (isPaused) {
        await resumeSession();
        toast.success('Resumed — timer running');
      } else {
        await pauseSession();
        toast.info('Paused — this time won\'t count toward your hours');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="timer-widget" variant="outline" size="sm"
          className={cn('gap-2 font-mono tabular-nums', activeSession && (isPaused ? 'timer-paused' : 'timer-running'))}>
          {activeSession ? (
            isPaused ? (
              <><span data-testid="timer-state-paused" className="inline-block h-2 w-2 rounded-full bg-amber-400" />{fmtElapsed(elapsed)} · Paused</>
            ) : (
              <><span data-testid="timer-state-running" className="pulse-dot inline-block h-2 w-2 rounded-full bg-emerald-400" />{fmtElapsed(elapsed)}</>
            )
          ) : (
            <><Timer className="h-4 w-4" /> Clock In</>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        {activeSession ? (
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold">{activeSession.category}</p>
                {isPaused && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-500 dark:text-amber-300">Paused</span>}
              </div>
              <p className="font-mono text-2xl font-bold tabular-nums">{fmtElapsed(elapsed)}</p>
              <p className="text-xs text-muted-foreground">Active time only — pauses don't count.</p>
              {activeSession.product_name && <p className="text-xs text-muted-foreground">Product: {activeSession.product_name}</p>}
            </div>
            <div className="flex gap-2">
              <Button data-testid="timer-pause-resume-button" onClick={handlePauseResume} disabled={busy} variant="outline"
                className={cn('flex-1 gap-2', isPaused ? 'timer-running' : 'timer-paused')}>
                {isPaused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}
              </Button>
              <Button data-testid="timer-clock-out-button" onClick={handleClockOut} disabled={busy} className="flex-1 gap-2" variant="destructive">
                <Square className="h-4 w-4" /> Clock Out
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-semibold">Start a work session</p>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="timer-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(stagesData.time_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="timer-notes-input" placeholder="What are you working on? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            <Button data-testid="timer-clock-in-button" onClick={handleClockIn} disabled={busy} className="w-full gap-2 timer-running">
              <Play className="h-4 w-4" /> Clock In
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const SidebarContent = ({ onNavigate }) => (
  <div className="flex h-full flex-col">
    <div className="flex items-center gap-2 px-4 py-5">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Kanban className="h-4 w-4" />
      </div>
      <div>
        <p data-testid="sidebar-app-name" className="text-sm font-bold leading-none tracking-tight">ChromiumHQ</p>
        <p className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">Solo Command Center</p>
      </div>
    </div>
    <nav className="flex-1 space-y-0.5 px-2">
      {NAV.map(({ to, label, icon: Icon, testId, accent }) => (
        <NavLink key={to} to={to} end={to === '/'} onClick={onNavigate} data-testid={testId}
          style={{ '--accent-section': accent }}
          className={({ isActive }) => cn(
            'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
            isActive
              ? 'bg-accent text-[hsl(var(--accent-section))] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-[hsl(var(--accent-section))] before:shadow-[0_0_8px_hsl(var(--accent-section))]'
              : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
          )}>
          <Icon className="h-[18px] w-[18px]" />
          {label}
        </NavLink>
      ))}
    </nav>
    <p className="px-4 py-4 text-[10px] text-muted-foreground">Private tool · one operator</p>
  </div>
);

export default function Layout({ children }) {
  const { theme, setTheme, videoTheme, setVideoTheme, logout } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const logoutTimer = useRef(null);
  const accent = accentForPath(location.pathname);
  const videoThemeLabel = videoTheme === 'cinematic' ? 'Cinematic' : 'Original';

  useEffect(() => () => clearTimeout(logoutTimer.current), []);

  const handleQuickLogout = () => {
    if (!confirmLogout) {
      // First click arms a brief inline confirm so a stray click never logs out
      setConfirmLogout(true);
      clearTimeout(logoutTimer.current);
      logoutTimer.current = setTimeout(() => setConfirmLogout(false), 3000);
      return;
    }
    clearTimeout(logoutTimer.current);
    logout();
    navigate('/login');
  };

  return (
    <div className="relative z-10 min-h-screen">
      <aside className="glass-chrome fixed inset-y-0 left-0 z-30 hidden w-[240px] border-r lg:block">
        <SidebarContent />
      </aside>
      <div className="lg:pl-[240px]">
        <header className="glass-chrome sticky top-0 z-20 flex h-14 items-center gap-3 border-b px-4">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="lg:hidden"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SidebarContent onNavigate={() => setSheetOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex-1" />
          <TimerWidget />
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="video-theme-toggle"
                  aria-label={`Background theme: ${videoThemeLabel}. Click to switch.`}
                  className="glass-icon-btn"
                  onClick={() => setVideoTheme(videoTheme === 'original' ? 'cinematic' : 'original')}
                >
                  <Clapperboard className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent data-testid="video-theme-tooltip">Theme: {videoThemeLabel}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="header-logout-button"
                  aria-label={confirmLogout ? 'Click again to confirm logout' : 'Log out'}
                  className={cn('glass-icon-btn', confirmLogout && 'is-danger px-3')}
                  onClick={handleQuickLogout}
                >
                  <LogOut className="h-4 w-4" />
                  {confirmLogout && <span data-testid="header-logout-confirm" className="text-xs font-semibold">Log out?</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent>{confirmLogout ? 'Click again to confirm' : 'Log out'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button data-testid="theme-toggle-switch" variant="ghost" size="icon" aria-label="Toggle theme"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </header>
        <main style={{ '--accent-section': accent }} className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
